import React, { useEffect, useState } from 'react';
import { Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getEstadisticasArbitralesCompeticion, getEstadisticasArbitro } from '../../services/arbitroEstadisticasService';
import { aggregateRefereeStatistics } from '../../helpers/arbitroEstadisticasHelper';

const METRICS = [['amarillas', 'yellow'], ['segundasAmarillas', 'secondYellow'], ['rojas', 'red']];

export default function RefereeCompetitionPanel({ competition, season, referee }) {
  const { t, i18n } = useTranslation('directories');
  const navigate = useNavigate();
  const [retry, setRetry] = useState(0);
  const [baseline, setBaseline] = useState({ key: '', status: 'loading' });
  const [detail, setDetail] = useState({ key: '', status: 'loading', documents: [] });
  const context = JSON.stringify([competition, season, retry]);
  const detailKey = JSON.stringify([competition, season, referee, retry]);
  useEffect(() => {
    if (!competition || !season) return undefined;
    let stale = false;
    setBaseline({ key: context, status: 'loading' });
    getEstadisticasArbitralesCompeticion(navigate, competition, season)
      .then((data) => { if (!stale) setBaseline({ key: context, status: 'success', data }); })
      .catch(() => { if (!stale) setBaseline({ key: context, status: 'error' }); });
    return () => { stale = true; };
  }, [competition, season, context, navigate]);
  useEffect(() => {
    if (!competition || !season || !referee) return undefined;
    let stale = false;
    setDetail({ key: detailKey, status: 'loading', documents: [] });
    getEstadisticasArbitro(referee, navigate, competition, season)
      .then((documents) => {
        if (!stale) setDetail({ key: detailKey, status: 'success', documents: Array.isArray(documents)
          ? documents.filter((doc) => String(doc.idTemporada) === season && String(doc.idCompeticion) === competition &&
            String(doc.idArbitro) === referee) : [] });
      }).catch(() => { if (!stale) setDetail({ key: detailKey, status: 'error', documents: [] }); });
    return () => { stale = true; };
  }, [competition, season, referee, detailKey, navigate]);
  const current = baseline.key === context;
  const data = current && baseline.status === 'success' ? baseline.data : null;
  const summary = detail.key === detailKey ? aggregateRefereeStatistics(detail.documents) : null;
  const fmt = (value) => value == null || !Number.isFinite(value) ? '—' :
    new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 }).format(value);
  const total = (cards) => METRICS.reduce((sum, [field]) => sum + Number(cards?.[field] || 0), 0);
  return <section className="dr-panel" aria-label={t('competitionBaseline')}>
    <h2>{t('competitionBaseline')}</h2>
    {!competition || !season ? <p>{t('chooseCompetitionSeason')}</p> : <>
      {(!current || baseline.status === 'loading') && <p role="status">{t('loading')}</p>}
      {current && baseline.status === 'error' && <p role="alert">{t('loadError')} <Button onClick={() => setRetry((value) => value + 1)}>{t('retry')}</Button></p>}
      {current && baseline.status === 'success' && !data && <p>{t('baselineNotGenerated')}</p>}
      {data && <>
        <p>{t('baselineSample', { matches: data.partidos, season })}</p>
        <p className="dr-muted">{data.cobertura?.completa ? t('coverageComplete') : t('coveragePartial')}
          {' · '}{t('coverageCounts', { covered: data.cobertura?.partidosConActa ?? '—', expected: data.cobertura?.partidosCalendario ?? '—' })}
          {' · '}{t('excludedReports', { count: data.cobertura?.actasExcluidas ?? 0 })}</p>
        <p className="dr-muted">{t('baselineGenerated', { date: data.generadoEn ? new Date(data.generadoEn).toLocaleString(i18n.language) : '—' })}</p>
        <div className="dr-table-scroll" role="region" tabIndex={0} aria-label={t('comparisonRates')}>
          <table className="dr-table"><thead><tr><th>{t('comparisonRates')}</th><th>{t('competitionMean')}</th>
            {referee && <><th>{summary?.nombre || t('referees')}</th><th>{t('difference')}</th></>}</tr></thead><tbody>
            {[...METRICS, ['total', 'cardsPerMatch']].map(([field, label]) => {
              const mean = data.partidos > 0 ? (field === 'total' ? total(data.tarjetas) : data.tarjetas?.[field]) / data.partidos : null;
              const rate = summary?.partidos.total > 0 ? (field === 'total' ? total(summary.tarjetas.total) : summary.tarjetas.total[field]) / summary.partidos.total : null;
              const difference = rate != null && mean != null ? rate - mean : null;
              return <tr key={field}><th scope="row">{t(label)}</th><td>{fmt(mean)}</td>
                {referee && <><td>{fmt(rate)}</td><td>{difference != null && difference > 0 ? '+' : ''}{fmt(difference)}</td></>}</tr>;
            })}
          </tbody></table>
        </div>
      </>}
      {referee && <>
        {detail.key !== detailKey || detail.status === 'loading' ? <p role="status">{t('loading')}</p> :
          detail.status === 'error' ? <p role="alert">{t('loadError')} <Button onClick={() => setRetry((value) => value + 1)}>{t('retry')}</Button></p> :
            <><p>{t('refereeSample', { matches: summary?.partidos.total ?? 0 })}</p>
              <h3>{t('recentMatches')}</h3>
              {summary?.partidosDetalle.length ? <ul className="dr-recent-matches">{summary.partidosDetalle.slice(0, 5).map((match, index) =>
                <li key={`${match.codActa || match.idPartido}:${index}`}>{match.fecha || '—'} · {match.equipoLocal || '—'} {match.golesLocal ?? '—'}–{match.golesVisitante ?? '—'} {match.equipoVisitante || '—'}</li>)}</ul>
                : <p>{t('noRecentMatches')}</p>}</>}
      </>}
    </>}
    <p className="dr-note">{t('baselineHint')}</p>
  </section>;
}
